import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { CalendarDays, CalendarCheck, LayoutDashboard, Layers, Sparkles, FileText, HelpCircle, Library, Images, Users, Settings, ShieldCheck, ScrollText, Box, LogOut, UserCog, Mail } from "lucide-react";
import { useAuth } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Seo } from "@/components/site/Ui";
import { formatDateTime } from "@/lib/format";

const LINKS = [
  ["/admin", "Dashboard", LayoutDashboard, null],
  ["/admin/agenda", "Agenda", CalendarDays, "agenda.read"],
  ["/admin/email", "Email inviate", Mail, "agenda.read"],
  ["/admin/apertura", "Giorni di apertura", CalendarCheck, "schedule.read"],
  ["/admin/categorie", "Categorie", Layers, "content.read"],
  ["/admin/trattamenti", "Trattamenti", Sparkles, "content.read"],
  ["/admin/pagine", "Pagine", FileText, "content.read"],
  ["/admin/faq", "FAQ", HelpCircle, "content.read"],
  ["/admin/glossario", "Glossario", Library, "content.read"],
  ["/admin/galleria", "Galleria", Images, "gallery.read"],
  ["/admin/professionisti", "Professionisti", Users, "staff.read"],
  ["/admin/risorse", "Cabine e risorse", Box, "resources.read"],
  ["/admin/impostazioni", "Impostazioni", Settings, "settings.write"],
  ["/admin/utenti", "Utenti", UserCog, "users.manage"],
  ["/admin/privacy", "Privacy e dati", ShieldCheck, "privacy.manage"],
  ["/admin/registro", "Registro attività", ScrollText, "audit.read"],
];

const ROLE = { owner: "Proprietario", secretary: "Segreteria", content: "Gestore contenuti" };

function Forbidden() {
  return <div className="card-soft p-8" data-testid="admin-forbidden"><h1 className="text-3xl">Accesso non consentito</h1><p className="mt-2 text-sm">Il tuo ruolo non dispone dei permessi per questa sezione.</p></div>;
}

export default function AdminLayout() {
  const { user, can, logout, refresh } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => {
    if (user === null) refresh();
  }, [user, refresh]);
  if (user === null) return <p className="p-10">Verifica sessione…</p>;
  if (!user) return <Navigate to="/admin/login" replace />;
  const route = LINKS.find(([to]) => to !== "/admin" && pathname.startsWith(to));
  const allowed = !route || !route[3] || can(route[3]);
  return (
    <div className="flex min-h-screen bg-[#F7F6F1]">
      <Seo title="Pannello" noindex />
      <aside className="hidden w-64 shrink-0 flex-col bg-[var(--ink)] text-[var(--ivory)] md:flex">
        <Link to="/" className="flex items-center gap-2 px-6 py-6"><img src="/brand/logo-morphe.webp" alt="Morphe Aesthetic" className="h-4 w-auto brightness-0 invert" /><span className="text-xs uppercase tracking-widest text-[var(--brass)]">Admin</span></Link>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-6" aria-label="Pannello">
          {LINKS.filter(([, , , p]) => !p || can(p)).map(([to, label, Icon]) => (
            <NavLink key={to} end={to === "/admin"} to={to} className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`} data-testid={`admin-nav-${to.split("/")[2] || "dashboard"}`}>
              <Icon size={16} />{label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-4 border-b border-[var(--line)] bg-white px-5 py-3">
          <select className="field max-w-[12rem] !py-2 text-sm md:hidden" onChange={(e) => (window.location.href = e.target.value)} value="" aria-label="Sezione" data-testid="admin-mobile-nav">
            <option value="">Menu…</option>
            {LINKS.filter(([, , , p]) => !p || can(p)).map(([to, label]) => <option key={to} value={to}>{label}</option>)}
          </select>
          <span className="hidden text-sm text-[var(--muted-ink)] md:block">Ambiente: {process.env.NODE_ENV === "production" ? "produzione" : "sviluppo"}</span>
          <div className="flex items-center gap-4 text-sm">
            <NavLink to="/admin/account" className="hover:underline" data-testid="admin-account-link">{user.name} · {ROLE[user.role]}</NavLink>
            <button onClick={logout} className="flex items-center gap-1 text-[var(--petrol)]" data-testid="admin-logout"><LogOut size={16} /> Esci</button>
          </div>
        </header>
        <main className="min-w-0 flex-1 p-5 sm:p-8">{allowed ? <Outlet /> : <Forbidden />}</main>
      </div>
    </div>
  );
}

export function AdminTitle({ title, children }) {
  return (
    <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
      <h1 className="text-4xl">{title}</h1>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => setD({}));
  }, []);
  if (!d) return null;
  const stats = [["Appuntamenti futuri", d.upcoming], ["Email non consegnate", d.email_failed], ["Contenuti da revisionare", d.pending_review], ["Trattamenti pubblicati", d.treatments], ["Casi galleria in bozza", d.gallery_drafts]].filter(([, v]) => v !== undefined);
  return (
    <>
      <AdminTitle title="Dashboard" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map(([k, v]) => <div key={k} className="card-soft p-6" data-testid={`stat-${k.replace(/\s/g, "-").toLowerCase()}`}><p className="text-sm text-[var(--muted-ink)]">{k}</p><p className="mt-2 font-serif text-5xl">{v}</p></div>)}
      </div>
      {d.today && (
        <section className="card-soft mt-8 p-6"><h2 className="text-2xl">Prossime 24 ore</h2>
          <ul className="mt-4 divide-y divide-[var(--line)]">{d.today.length === 0 && <li className="py-3 text-sm text-[var(--muted-ink)]">Nessun appuntamento.</li>}
            {d.today.map((a) => <li key={a.id} className="flex justify-between py-3 text-sm"><span>{formatDateTime(a.start_utc)} · {a.snapshot?.treatment_name}</span><span>{a.customer?.first_name} {a.customer?.last_name}</span></li>)}</ul>
        </section>
      )}
    </>
  );
}
